import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

/**
 * i18n-Konfiguration (react-i18next)
 *
 * Konventionen (gelten für alle Phasen der Internationalisierung):
 * - Sprache = URL-Präfix (Single Source of Truth):
 *   Deutsch auf Root (monemee.com/...), Englisch unter /en/ (monemee.com/en/...).
 *   Es gibt KEINEN separaten Sprach-State, der von der URL abweichen kann.
 * - Übersetzungen liegen in client/src/locales/{lng}/{namespace}.json und
 *   werden per Dynamic Import lazy geladen (Code-Splitting pro Namespace).
 * - Namespaces orientieren sich an den Seiten-Bereichen:
 *   common, auth, store, earnings, promotion, settings, landing, legal,
 *   checkout, messages, dashboard, ...
 * - Key-Naming: camelCase, flach gruppiert nach Komponente/Sektion,
 *   z.B. t('nav.earnings'), t('productForm.priceLabel').
 * - Interpolation: t('greeting', { name }) — kein String-Concat im JSX.
 */

export const SUPPORTED_LANGS = ['de', 'en'];
export const DEFAULT_LANG = 'de';

/** localStorage-Key für die explizite Sprachwahl des Besuchers */
export const LANG_STORAGE_KEY = 'monemee_lang';

/**
 * Ermittelt die Sprache aus einem Pfad.
 * '/en' und '/en/...' → 'en', alles andere → 'de'.
 */
export function getLangFromPath(pathname) {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : DEFAULT_LANG;
}

/**
 * Entfernt das Sprachpräfix aus einem Pfad → sprachneutraler Pfad.
 * '/en/login' → '/login', '/login' → '/login', '/en' → '/'
 */
export function stripLangPrefix(pathname) {
  if (pathname === '/en') return '/';
  if (pathname.startsWith('/en/')) return pathname.slice(3);
  return pathname;
}

/**
 * Baut den Pfad für eine Zielsprache aus einem sprachneutralen Pfad.
 * ('en', '/login') → '/en/login', ('de', '/login') → '/login'
 *
 * Hinweis: SEO-Landingpages haben pro Sprache EIGENE Slugs — die werden
 * in routes/slugMap.js übersetzt, bevor diese Funktion greift.
 */
export function buildLangPath(lang, neutralPath) {
  return lang === 'en' ? `/en${neutralPath === '/' ? '' : neutralPath}` || '/en' : neutralPath;
}

/** Gespeicherte Sprachpräferenz lesen (null wenn nie gesetzt). */
export function getStoredLang() {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    return SUPPORTED_LANGS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Sprachpräferenz speichern (vom Switcher und der Erst-Erkennung). */
export function storeLang(lang) {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // localStorage nicht verfügbar (Private Mode etc.) — kein Problem
  }
}

/** Grobe Bot-Erkennung — Crawler dürfen NIE umgeleitet werden (SEO/hreflang). */
export function isBot() {
  return /bot|crawl|spider|slurp|bingpreview|googlebot|duckduckbot|baiduspider|yandex|facebookexternalhit|twitterbot|linkedinbot|lighthouse|headless|reactsnap/i.test(
    navigator.userAgent
  );
}

/**
 * Einmalige Sprach-Erkennung beim App-Start (vor dem React-Mount aufrufen).
 *
 * Regeln (SEO-sicher):
 * - Bots werden nie umgeleitet → beide Sprachversionen bleiben crawlbar.
 * - Nur die Startseite ('/') wird umgeleitet — tiefe Links bleiben, wie
 *   sie geteilt wurden (deutsche Slugs ≠ englische Slugs).
 * - Explizite Wahl (localStorage) gewinnt immer vor Browser-Sprache.
 *
 * @returns {string|null} Ziel-URL für einen Redirect oder null.
 */
export function resolveInitialLangRedirect() {
  if (isBot()) return null;

  const { pathname, search, hash } = window.location;
  const pathLang = getLangFromPath(pathname);
  const stored = getStoredLang();

  // Nur auf der Startseite automatisch umleiten
  if (stripLangPrefix(pathname) !== '/') return null;

  if (stored) {
    // Wiederkehrender Besucher: gespeicherte Wahl anwenden
    if (stored !== pathLang) {
      return buildLangPath(stored, '/') + search + hash;
    }
    return null;
  }

  // Erstbesuch: Browser-Sprache prüfen. Nur de→en automatisch —
  // wer /en explizit öffnet, bleibt dort.
  //
  // Bewusst OHNE storeLang(): Eine Vermutung aus navigator.languages ist keine
  // Entscheidung des Nutzers. Wurde sie gespeichert, griff beim nächsten Besuch
  // der stored-Zweig oben — die Vermutung war damit dauerhaft festgeschrieben
  // und ließ sich nur noch über den Switcher korrigieren. Ohne Speichern wird
  // bei jedem Besuch neu ausgewertet, und eine echte Wahl im LanguageSwitcher
  // (der als einziger storeLang aufruft) gewinnt weiterhin immer.
  if (pathLang === DEFAULT_LANG) {
    const browserLangs = navigator.languages || [navigator.language || ''];
    const prefersGerman = browserLangs.some((l) => l && l.toLowerCase().startsWith('de'));
    const prefersEnglish = browserLangs.some((l) => l && l.toLowerCase().startsWith('en'));
    if (!prefersGerman && prefersEnglish) {
      return buildLangPath('en', '/') + search + hash;
    }
  }

  return null;
}

/**
 * i18next initialisieren. Die Sprache kommt aus dem URL-Präfix —
 * danach ändert sie sich nur durch echte Navigation (Switcher = Hard-Link).
 */
export function initI18n(lang) {
  i18n
    .use(resourcesToBackend((lng, ns) => import(`../locales/${lng}/${ns}.json`)))
    .use(initReactI18next)
    .init({
      lng: lang,
      fallbackLng: DEFAULT_LANG,
      supportedLngs: SUPPORTED_LANGS,
      ns: ['common'],
      defaultNS: 'common',
      interpolation: {
        escapeValue: false, // React escaped selbst
      },
      react: {
        useSuspense: true, // Namespaces laden über den bestehenden Suspense-Fallback
      },
    });

  return i18n;
}

export default i18n;
